import { CircularProgress } from "@mui/material";
import { useEffect } from "react";
import { createContext, useContext, useState } from "react";
import i18n from "translation";
import * as Sentry from "@sentry/react";
import APIRequest, { APIError, registerAPIRequestErrorCallback, unregisterAPIRequestErrorCallback } from "./APIRequest";
import { loginCurrentUser, logoutCurrentUser, loginByCachedSession, currentUser, InfegyUser, sessionToken, clearSessionToken, wasSessionExpired, setCurrentUser, UserPreference, loginByTwoFactor, clearExpiredSession, loginBySSO } from "./InfegyUser";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

// React hooks and context
// see: https://usehooks.com/useAuth/

// This file provides a React Hook and Context Provider for using API authentication
// To get the current user, authentication info, and actions to manage it, use the useAuth() hook in your component
// For example:
//   let auth = useAuth();
//   consoIe.log('Hello ', auth.user?.name, '!');

export default function useAuth() {
  return useContext(authContext);
}

interface authProvider {
  user: InfegyUser | undefined;
  token: string | undefined;
  isAuthenticated: () => boolean | undefined;
  logIn: (email: string, password: string) => Promise<boolean>;
  logInTwoFactor: (token: string, code: string) => Promise<boolean>;
  logInSSO: (id_token: string, provider: string) => Promise<boolean>;
  logOut: () => Promise<boolean>;
  clearSession: () => void;
  updateFromCurrentUser: () => void;
  sessionExpired: () => boolean;
  updateUser: () => void;
}

export function getUserLanguage(user: InfegyUser | undefined): string {
  // Get the user's preferred language, or default to browser preference, or fallback to "en".
  const language = user?.getPreference(UserPreference.LOCALE).split("-")[0] || navigator.language.split("-")[0];
  if (!language || typeof language !== "string" || language.length != 2) {
    return "en";
  }
  return language;
}

export function useProvideAuth(): authProvider {
  const { i18n } = useTranslation();

  const [user, _setUser] = useState<InfegyUser | undefined>(undefined);
  const [token, setToken] = useState<string | undefined>(sessionToken());

  function setUser(user: InfegyUser | undefined) {
    if (user) {
      Sentry.setUser({
        email: user?.email,
        id: user?.id,
        username: user?.name
      });
    } else {
      Sentry.setUser(null);
    }
    
    _setUser(user);
  }

  // Register for API auth errors
  useEffect(() => {
    const handleAuthError = (error: APIError) => {
      // If we get a 401 error and we have a user or token, clear the session
      if (error.code === 401 && (user !== undefined || sessionToken())) {
        clearSession(true);
      }
    };

    registerAPIRequestErrorCallback(handleAuthError);
    
    return () => {
      unregisterAPIRequestErrorCallback(handleAuthError);
    };
  }, [user]);

  const isAuthenticated = (): boolean | undefined => {
    let result = undefined;
    
    // Get current token directly from storage
    const currentToken = sessionToken();
    
    if (currentToken === "" || user !== undefined) {
      result = Boolean(user !== undefined && user.id);
    }
    
    return result;
  };
  
  const postLogin = () => {
    setUser(currentUser());
    setToken(sessionToken());
    const language = getUserLanguage(currentUser());
    if (language) {
      i18n.changeLanguage(language);
    }
    return true;
  };

  const logIn = (email: string, password: string) => {
    return loginCurrentUser(email, password).then((response) => {
      postLogin();
    });
  };

  const logInTwoFactor = (token: string, code: string) => {
    return loginByTwoFactor(token, code).then((response) => {
      postLogin();
    });
  };
  
  const logInSSO = (id_token: string, provider: string) => {
    return loginBySSO(id_token, provider).then((response) => {
      postLogin();
    });
    
  };

  const logOut = async () => {
    return await logoutCurrentUser().then((response) => {
      clearSessionToken(false);
      return true;
    }).catch((reason: APIError) => {
      // Ignore, log out, anyway
      clearSessionToken(false);
      return true;
    });
  };

  // Removes session state for this user _WITHOUT_ LOGGING OUT!
  // Use with caution!
  const clearSession = (expired: boolean = false) => {
    setToken(undefined);
    setUser(undefined);
    if(expired) {
      clearExpiredSession();
    } else {
      clearSessionToken();
    }
  }

  const updateFromCurrentUser = () => {
    if (token !== sessionToken())
      setToken(sessionToken());
    setUser(currentUser());
  };

  const sessionExpired = (): boolean => {
    return wasSessionExpired();
  }

  const updateUser = async () => {
    return APIRequest("me").then((response: any) => {
      // The session re-use succeeded!
      // We can populate user info
      setCurrentUser(new InfegyUser(response));
      setUser(currentUser());
      return true;
    });
  }

  return {
    user,
    token,
    isAuthenticated,
    logIn,
    logInTwoFactor,
    logInSSO,
    logOut,
    clearSession,
    updateFromCurrentUser,
    sessionExpired,
    updateUser
  };
}

const authContext = createContext<authProvider>(undefined!);

export function ProvideAuth({ children }: any) {
  const auth = useProvideAuth();
  const [badSession, setBadSession] = useState(false);

  useEffect(() => {
    const getToken = async () => {
      // We have a cached sessionToken, but are not authenticated
      // Try to use the cached token
      await loginByCachedSession().then(() => {
        // Cached session token is usable!
        auth.updateFromCurrentUser();
        const language = getUserLanguage(currentUser());
        if (language) {
          i18n.changeLanguage(language);
        }
      }).catch((reason: any) => {
        // Token is probably expired, ignore
        setBadSession(true);
      });
    };

    if (sessionToken() && currentUser() === undefined) {
      getToken();
    }
  }, [auth]);
  
  if (sessionToken() && currentUser() === undefined && !badSession) {
    return <authContext.Provider value={auth}>
      <div style={{ display: "flex", width: "100vw", height: "100vh", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
        &nbsp; {i18n.t("login.form.login-in-flight")}
      </div>
    </authContext.Provider>
  }

  return (
    <authContext.Provider value={auth} >
      {children}
    </authContext.Provider>
  );
}
